import { apiCall, apiCallWithCsrfRefresh } from "./api";
import { anonDraftPreferencesForGenerate } from "./preferencesApi";
import { PREFERENCES_SAVED_EVENT } from "./preferencesSaved";
import { JOURNEY_CHANGED_EVENT } from "./journeyApi";
import type {
  AddCandidate,
  EngineAlternative,
  FuelPlanViewModel,
  SharedPlanView,
  SlotKey
} from "./fuelPlanTypes";
import type { PlanCustomizationState } from "./planCustomizations";

const BASE = "/api/fuel-plans";

export type AddCandidatesResponse = {
  ready: boolean;
  slot?: SlotKey;
  scope?: "slot" | "all";
  reason?: string;
  candidates: AddCandidate[];
};

/**
 * Fetch Add-product search candidates for a slot (A1). Returns flavour-family
 * cards from the same eligible catalog as swap suggestions; the modal filters
 * by name client-side. `scope: "all"` opens the whole eligible catalog.
 * Not cached — the catalog can change and the result set is small per slot.
 */
export async function loadAddCandidates(
  planId: string,
  slot: SlotKey,
  opts: { scope?: "slot" | "all"; signal?: AbortSignal } = {}
): Promise<AddCandidatesResponse> {
  const params = new URLSearchParams({ slot });
  if (opts.scope) params.set("scope", opts.scope);
  return apiCall<AddCandidatesResponse>(
    `${BASE}/${encodeURIComponent(planId)}/add-candidates?${params.toString()}`,
    { signal: opts.signal }
  );
}

export type ProductAlternativesResponse = {
  ready: boolean;
  reason?: string;
  alternatives: EngineAlternative[];
};

/**
 * Fetch the cross-product swap lineup for one product in a slot. Called at
 * add-time so the chosen product's alternatives can be frozen onto the
 * `AddedLine` — the Customize modal then offers swaps on added products exactly
 * like engine ones. `workoutId` scopes the policy gate for workout-scoped adds.
 */
export async function loadProductAlternatives(
  planId: string,
  productId: string,
  slot: SlotKey,
  opts: { workoutId?: string; signal?: AbortSignal } = {}
): Promise<ProductAlternativesResponse> {
  const params = new URLSearchParams({ slot, productId });
  if (opts.workoutId) params.set("workoutId", opts.workoutId);
  return apiCall<ProductAlternativesResponse>(
    `${BASE}/${encodeURIComponent(planId)}/product-alternatives?${params.toString()}`,
    { signal: opts.signal }
  );
}

export type GeneratePayload = {
  /**
   * Plan window length in weeks, for a FRESH generate. Omitted on regenerate:
   * both regenerate modes derive the window from the source plan server-side,
   * so a client value would be ignored (and conflating it with the ATC
   * buy-multiplier is exactly the bug that shrank/leaked regenerated plans).
   */
  periodCount?: number;
  /**
   * Required when `source === "live"` (or omitted). Ignored when
   * `source === "snapshot"`.
   */
  plannedWorkoutIds?: string[];
  regenerationToken?: string;
  /**
   * SPA plan anchor (ISO UTC midnight). Persisted onto the resulting FuelPlan
   * row's `planAnchoredAt`. Until later slices expose a user control, callers
   * pass today's UTC calendar date; the server falls back to that anyway if
   * the field is absent.
   */
  planAnchoredAt?: string;
  /**
   * Calendar-day offset from anchor to plan-active start. Defaults to 7
   * server-side when absent.
   */
  planStartOffsetDays?: number;
  /**
   * PR-D: regenerate source. `"live"` (default) re-reads current planner
   * rows; `"snapshot"` re-runs the engine against the source plan's frozen
   * inputs. Wired by the regenerate modal when the calendar has drifted.
   */
  source?: "live" | "snapshot";
  /** Required when `source === "snapshot"`. The FuelPlan to regenerate from. */
  sourcePlanId?: string;
  /**
   * A-9 lead-capture email. Required by the server for anon principals (the
   * server returns `error: "email_required"` if missing); ignored for
   * signed-in customers (`Customer.email` is the source of truth). The SPA
   * only includes this field on anon generates (journey-rail box/kit paths).
   */
  email?: string;
};

export type GenerateResponse = {
  fuelPlanRequestId: string;
  /**
   * `processing` — dispatched to the remote engine; poll `getFuelPlanRequestStatus`
   * until terminal. `completed` — an identical plan already existed; `fuelPlanId`
   * is set and the SPA can navigate straight to it.
   */
  status: "processing" | "completed";
  /** Set only when `status === "completed"`. */
  fuelPlanId: string | null;
  reused: boolean;
  /** The plan this build was fired FROM, when it replaced one. Lets the page a
   *  customer is looking at follow its own rebuild. Null for a fresh generate
   *  and for every idempotent reuse. */
  sourcePlanId?: string | null;
};

export async function generateFuelPlan(
  payload: GeneratePayload,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<GenerateResponse> {
  // Slice C (preferences-outbound-alignment): attach the anon browser-draft
  // preferences so a guest's generate carries their dietary needs / exclusions
  // into the engine payload. Safe on customer calls too — see
  // anonDraftPreferencesForGenerate.
  const preferences = anonDraftPreferencesForGenerate();
  return apiCallWithCsrfRefresh<GenerateResponse>(
    `${BASE}/generate`,
    {
      method: "POST",
      body: preferences ? { ...payload, preferences } : payload,
      csrfToken
    },
    refreshCsrf
  );
}

export type FuelPlanRequestStatus = {
  requestId: string;
  /** `processing` | `requested` | `completed` | `failed` | `expired`. */
  status: string;
  /** Set once the plan row exists — even if it has since been superseded. */
  fuelPlanId: string | null;
  /**
   * The plan row's own status (`completed` | `purchased` | `delivered` |
   * `expired` | …). Null while no plan has landed, when a supersede deleted
   * the row, or from a pre-planStatus server. A `completed` request whose
   * plan is missing/expired was replaced by a newer regenerate — terminal
   * `superseded`, never keep polling.
   */
  planStatus?: string | null;
  /**
   * Curated customer-facing copy when the request failed. Always one of a
   * small closed set of strings — never raw upstream text. Null while the
   * request is in flight or completed.
   */
  failureSummary: string | null;
};

/**
 * Three-way terminal classification of a request-status poll (shared by the
 * PlanPending screen and useGeneratingPlanWatcher so every poller agrees):
 *   - `live`       — the request completed AND its plan is still openable
 *                    (completed/purchased/delivered). Navigate to it.
 *   - `superseded` — the request completed but its plan was replaced by a
 *                    newer regenerate (expired or deleted). Terminal — stop
 *                    polling, point the user at their latest plans. Keeping
 *                    polling here was the "stuck on Building your fueling
 *                    plan" bug.
 *   - `failed` / `expired` — the request itself is unbuildable.
 *   - `pending`    — processing/requested; keep polling.
 */
export type RequestStatusClass =
  | { kind: "live"; fuelPlanId: string }
  | { kind: "superseded"; fuelPlanId: string | null }
  | { kind: "failed"; failureSummary: string | null }
  | { kind: "expired" }
  | { kind: "pending" };

const LIVE_PLAN_STATUSES = new Set(["completed", "purchased", "delivered"]);

export function classifyRequestStatus(s: FuelPlanRequestStatus): RequestStatusClass {
  if (s.status === "completed") {
    // `planStatus` may be absent from a pre-planStatus server; a set
    // `fuelPlanId` then already implies the plan is live (old servers only
    // sent the id while `plan.status === "completed"`).
    const live =
      s.fuelPlanId != null && (s.planStatus == null || LIVE_PLAN_STATUSES.has(s.planStatus));
    if (live) return { kind: "live", fuelPlanId: s.fuelPlanId as string };
    return { kind: "superseded", fuelPlanId: s.fuelPlanId ?? null };
  }
  if (s.status === "failed") return { kind: "failed", failureSummary: s.failureSummary ?? null };
  if (s.status === "expired") return { kind: "expired" };
  return { kind: "pending" };
}

/** Poll a dispatched request until it reaches a terminal state. */
export async function getFuelPlanRequestStatus(
  requestId: string,
  signal?: AbortSignal
): Promise<FuelPlanRequestStatus> {
  return apiCall<FuelPlanRequestStatus>(
    `${BASE}/requests/${encodeURIComponent(requestId)}`,
    { signal }
  );
}

/**
 * Per-session cache for `GET /api/fuel-plans/:id`. The endpoint hydrates the
 * full product catalog and runs a drift diff, so it's expensive to recompute;
 * naive re-mounts (route back-button, modal-driven `useFuelPlan` callers,
 * share-modal previews) would all retrigger it.
 *
 * Two layers:
 *   1. `cache` — resolved payloads, valid for `PLAN_CACHE_TTL_MS`. Hits return
 *      synchronously.
 *   2. `inflight` — promises for requests that are mid-flight, so concurrent
 *      callers share one network round-trip rather than firing N parallel GETs.
 *
 * Invalidated explicitly by `saveFuelPlanCustomizations` (the plan view's
 * `customizations` field changes on save and the server re-normalises before
 * persisting, so subsequent loads must hit the wire).
 */
const PLAN_CACHE_TTL_MS = 60_000;
type CacheEntry = { data: FuelPlanViewModel; ts: number };
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<FuelPlanViewModel>>();

export function invalidateFuelPlanCache(id?: string): void {
  if (id === undefined) {
    cache.clear();
    return;
  }
  cache.delete(id);
}

// Slice D (preferences-outbound-alignment): a preferences edit changes the
// server-computed plan-input freshness (`preferences_changed`), and because a
// box is scored against the plan's FROZEN dietary needs, a regenerate changes
// the plan itself. Drop the whole plan cache on a prefs save so the next
// plan/journey read refetches fresh freshness instead of a stale cached view.
// The plan-detail screen refetches its own view directly on this event too;
// this covers the journey list and any other cached plan reads. Guarded for
// non-browser (test/SSR) import.
if (typeof window !== "undefined") {
  window.addEventListener(PREFERENCES_SAVED_EVENT, () => invalidateFuelPlanCache());
  // F-X1-2: content mutations broadcast JOURNEY_CHANGED and the server marks the
  // intersecting box plans stale — so the cached 60s plan view must drop too, or a
  // box opened within 60s of a workout edit shows pre-edit carb/sodium numbers.
  // Subscribing here makes `notifyJourneyChanged()` genuinely invalidate this cache
  // (as its call-site comments already claim) across ALL mutation paths — edit,
  // delete, event-date, role/window/skip — not just the create/swap sites that
  // happened to call invalidateFuelPlanCache() explicitly.
  window.addEventListener(JOURNEY_CHANGED_EVENT, () => invalidateFuelPlanCache());
}

export async function loadFuelPlan(id: string, signal?: AbortSignal): Promise<FuelPlanViewModel> {
  const cached = cache.get(id);
  if (cached && Date.now() - cached.ts < PLAN_CACHE_TTL_MS) {
    return cached.data;
  }
  let promise = inflight.get(id);
  if (!promise) {
    // Never attach the caller's AbortSignal to the shared network fetch. React
    // Strict Mode (and fast remounts) abort the first effect's signal while a
    // second subscriber is still waiting on the same inflight promise — killing
    // the fetch left the plan screen stuck on "couldn't load" with no error.
    promise = apiCall<FuelPlanViewModel>(`${BASE}/${encodeURIComponent(id)}`)
      .then((data) => {
        cache.set(id, { data, ts: Date.now() });
        return data;
      })
      .finally(() => {
        inflight.delete(id);
      });
    inflight.set(id, promise);
  }
  return awaitAbortable(promise, signal);
}

/** Reject when `signal` aborts; do not cancel the underlying shared request. */
function awaitAbortable<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) {
    return Promise.reject(new DOMException("The operation was aborted.", "AbortError"));
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(new DOMException("The operation was aborted.", "AbortError"));
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (err) => {
        signal.removeEventListener("abort", onAbort);
        reject(err);
      }
    );
  });
}

/**
 * Load a plan via a public share token (no auth) — backs the `/share/:token`
 * screen. Returns a trimmed, customization-aware DTO (see `SharedPlanView`),
 * NOT the full owner view model.
 */
export async function loadSharedFuelPlan(
  token: string,
  signal?: AbortSignal
): Promise<SharedPlanView> {
  return apiCall<SharedPlanView>(`${BASE}/share/${encodeURIComponent(token)}`, { signal });
}

/**
 * Result of asking the server to build a Shopify cart permalink for a shared
 * plan's box. `ready: false` means the box can't be ordered right now (shop
 * domain unresolvable, placeholder variant IDs, …) — the receiving page hides
 * the shop card rather than surfacing an error.
 */
export type SharedPlanCartResult = { ready: true; url: string } | { ready: false; reason?: string };

/**
 * `POST /api/fuel-plans/share/:token/cart` — public (token IS the capability;
 * no auth, no CSRF). Builds a product-only cart permalink for the shared box:
 * the recipient buys products, never the plan, so the permalink carries none
 * of the owner flow's plan-linking attributes. 400 = invalid token,
 * 410 = expired/revoked (both surface as `ApiError`).
 */
export async function createSharedPlanCartPermalink(
  token: string,
  signal?: AbortSignal
): Promise<SharedPlanCartResult> {
  return apiCall<SharedPlanCartResult>(`${BASE}/share/${encodeURIComponent(token)}/cart`, {
    method: "POST",
    signal
  });
}

/** Allowed lifetimes for a share token. Mirrors the server's allowlist. */
export const SHARE_TTL_DAY_OPTIONS = [7, 14, 30] as const;
export type ShareTtlDays = (typeof SHARE_TTL_DAY_OPTIONS)[number];

/**
 * Mint a share token for a plan the caller owns; the SPA builds the URL from
 * it. `ttlDays` optionally narrows the token lifetime (7/14/30); the server
 * defaults to 30 when omitted. `sharedAt` is the token's mint time (its `iat`,
 * ISO) — the receiving page's "Shared Jul 14 · link active 30 days" line.
 */
export async function createShareLink(
  id: string,
  csrfToken: string,
  refreshCsrf: () => Promise<string>,
  ttlDays?: ShareTtlDays
): Promise<{ token: string; ttlDays: number; sharedAt: string }> {
  return apiCallWithCsrfRefresh<{ token: string; ttlDays: number; sharedAt: string }>(
    `${BASE}/${encodeURIComponent(id)}/share`,
    {
      method: "POST",
      csrfToken,
      body: ttlDays !== undefined ? { ttlDays } : undefined
    },
    refreshCsrf
  );
}

/**
 * "Turn off previous links" — rotates the plan's server-side share nonce so
 * every previously minted (v2) share link stops working. 204 on success; the
 * caller must then drop ITS whole token cache (all TTLs) and re-mint, or the
 * modal would keep offering dead URLs. Legacy links minted before the nonce
 * scheme can't be revoked and age out naturally (≤30 days).
 */
export async function revokeShareLinks(
  id: string,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<void> {
  await apiCallWithCsrfRefresh<void>(
    `${BASE}/${encodeURIComponent(id)}/share/revoke`,
    { method: "POST", csrfToken },
    refreshCsrf
  );
}

/**
 * Persist the full customization overlay onto a fueling plan. Whole-document
 * replace — the caller sends complete `PlanCustomizationState` every time.
 *
 * Returns the state the server actually persisted: snapshots re-hydrated
 * against the live catalog (DB-canonical tags/macros/price). The caller folds
 * the enriched snapshots back into its optimistic overlay. `undefined` on
 * older servers that only return `{ ok: true }`.
 */
export async function saveFuelPlanCustomizations(
  id: string,
  state: PlanCustomizationState,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<PlanCustomizationState | undefined> {
  const res = await apiCallWithCsrfRefresh<{ ok: true; customizations?: PlanCustomizationState }>(
    `${BASE}/${encodeURIComponent(id)}/customizations`,
    { method: "PATCH", body: state, csrfToken },
    refreshCsrf
  );
  // The plan view's `customizations` field has changed (the server also re-
  // normalises before persisting), so any cached payload is stale — drop it
  // so the next `loadFuelPlan` fetches a fresh one.
  invalidateFuelPlanCache(id);
  return res.customizations;
}

export type BoxReviewSavedItem = {
  productId: string;
  verdict: "up" | "down";
  reasonChips: string[];
  note: string | null;
};

export type BoxReviewWriteItem = {
  productId: string;
  verdict: "up" | "down" | null;
  reasonChips: string[];
  note: string;
};

/** Existing thumbs for this box — hydrates the rate modal. */
export async function loadBoxReview(planId: string): Promise<{ items: BoxReviewSavedItem[] }> {
  return apiCall<{ items: BoxReviewSavedItem[] }>(
    `${BASE}/${encodeURIComponent(planId)}/box-review`
  );
}

/**
 * Persist dirty box-review thumbs in one shot. Skipped products are omitted
 * by the caller; `verdict: null` clears a previously saved family row.
 */
export async function saveBoxReview(
  planId: string,
  items: BoxReviewWriteItem[],
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<void> {
  await apiCallWithCsrfRefresh(
    `${BASE}/${encodeURIComponent(planId)}/box-review`,
    { method: "POST", body: { items }, csrfToken },
    refreshCsrf
  );
}
